import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  AccountType,
  GeneralLedgerSidepanel,
  TransactionTypeEnum,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import { clearExpandedGeneralLedgerTransactionState } from './store/slice';
import { useLazyGetGeneralLedgerExpandedTransactionQuery } from './store/services';
import { useLazyGetTransactionsAccountBalanceByIdQuery } from '../general-ledger/store/services';

import ROUTES from '../../routes';

const ExpandedGeneralLedgerTransaction = ({
  backLink,
}: {
  backLink: string;
}) => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);

  const {
    transactionsDictionaries: { transactionsTypes },
    transactions: {
      expandedGLTransaction,
      expandedGLTransactionDebitBalance,
      expandedGLTransactionCreditBalance,
    },
  } = useAppSelector((state) => state);

  const [
    getDebitAccountBalanceById,
    { isLoading: debitBalanceIsLoading, isFetching: debitBalanceIsFetching },
  ] = useLazyGetTransactionsAccountBalanceByIdQuery();
  const [
    getCreditAccountBalanceById,
    { isLoading: creditBalanceIsLoading, isFetching: creditBalanceIsFetching },
  ] = useLazyGetTransactionsAccountBalanceByIdQuery();

  const { glId } = useParams();
  const { search } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const linkedTransactionPath = useMemo(() => {
    const currentTransactionType = transactionsTypes?.find(
      (item) => item.id === expandedGLTransaction?.transaction?.typeId,
    );

    if (currentTransactionType) {
      let link = '#';
      if (currentTransactionType?.name === TransactionTypeEnum.INTERNAL) {
        link = ROUTES.INTERNAL_TRANSACTION_READ;
      } else if (
        currentTransactionType?.name === TransactionTypeEnum.INCOMING
      ) {
        link = ROUTES.INCOMING_TRANSACTION;
      } else if (
        currentTransactionType?.name === TransactionTypeEnum.OUTGOING
      ) {
        link = ROUTES.OUTGOING_TRANSACTION;
      } else if (
        currentTransactionType?.name === TransactionTypeEnum.CURRENCY_EXCHANGE
      ) {
        link = ROUTES.EXCHANGE_READ;
      } else if (currentTransactionType?.name === TransactionTypeEnum.NOSTRO) {
        link = ROUTES.NOSTRO_TRANSFER;
      }
      return `${generatePath(link, {
        transactionId: expandedGLTransaction?.parentTransactionId,
      })}${search || ''}`;
    }
    return undefined;
  }, [
    transactionsTypes,
    expandedGLTransaction?.transaction?.typeId,
    expandedGLTransaction?.parentTransactionId,
  ]);

  const handleClose = () => {
    setPanelIsOpen(false);

    setTimeout(() => {
      navigate(generatePath(`${backLink}${search || ''}`));
    }, 100);
  };

  const [getGeneralLedger, { isLoading, isFetching }] =
    useLazyGetGeneralLedgerExpandedTransactionQuery();

  const getTransaction = useCallback((id: string) => {
    getGeneralLedger(id);
  }, []);

  useEffect(() => {
    setPanelIsOpen(true);

    return () => {
      dispatch(clearExpandedGeneralLedgerTransactionState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (glId && expandedGLTransaction) {
      getDebitAccountBalanceById({
        id: expandedGLTransaction?.debitAccountId as string,
        query: `&currencyId=${expandedGLTransaction?.currencyId}`,
        typeAcc: AccountType.DEBIT,
      });
      getCreditAccountBalanceById({
        id: expandedGLTransaction?.creditAccountId as string,
        query: `&currencyId=${expandedGLTransaction?.currencyId}`,
        typeAcc: AccountType.CREDIT,
      });
    }
  }, [glId, expandedGLTransaction]);

  useEffect(() => {
    if (!expandedGLTransaction && glId) {
      getTransaction(glId);
    }
  }, [getTransaction, expandedGLTransaction, glId]);

  return (
    <GeneralLedgerSidepanel
      panelIsOpen={panelIsOpen}
      handleClose={handleClose}
      transaction={expandedGLTransaction}
      isLoading={isLoading || isFetching}
      title="GL Transaction"
      linkedTransactionPath={
        linkedTransactionPath?.includes('#') ? undefined : linkedTransactionPath
      }
      debitBalanceIsLoading={debitBalanceIsLoading || debitBalanceIsFetching}
      creditBalanceIsLoading={creditBalanceIsLoading || creditBalanceIsFetching}
      debitAccountBalance={expandedGLTransactionDebitBalance}
      creditAccountBalance={expandedGLTransactionCreditBalance}
      sidepanelExpandedKey="gl-transaction"
    />
  );
};

export default ExpandedGeneralLedgerTransaction;
